import { Box, TextField, Typography } from "@mui/material";
import React, { useCallback } from "react";
import { usePagesTranslation } from "../../../locales";
import { ACCENT, SURFACE } from "../../../../dark-theme/palette";

const GRID_LETTER_FONT_MIN = 6;
const GRID_LETTER_FONT_MAX = 40;

function clampInt(n, min, max) {
    const x = Math.round(Number(n));
    if (Number.isNaN(x)) return min;
    return Math.min(max, Math.max(min, x));
}

/**
 * ManualValidationInput Component
 *
 * A reusable component for manual validation text input in puzzle play pages.
 * Only renders when manualValidation is enabled.
 *
 * Optional: when `onGridLetterFontPxChange` is set (grid play), shows letter-size tuning and a gray admin panel.
 *
 * @param {boolean} manualValidation - Whether manual validation mode is enabled
 * @param {string} typedWord - The current value of the typed word
 * @param {function} onTypedWordChange - Handler for text input changes
 * @param {function} onTypedWordKeyDown - Handler for keyboard events (e.g., Enter key)
 * @param {string} userRole - User role (ADMIN, CREATOR, etc.) to display in help text
 * @param {number} [gridLetterFontPx] - Current grid letter size in px (when tuning)
 * @param {function} [onGridLetterFontPxChange] - Set grid letter size (enables tuner + gray panel)
 */
function ManualValidationInput({
    manualValidation,
    typedWord,
    onTypedWordChange,
    onTypedWordKeyDown,
    userRole,
    gridLetterFontPx,
    onGridLetterFontPxChange,
}) {
    const { t } = usePagesTranslation();
    const handleLetterFontChange = useCallback(
        (e) => {
            if (typeof onGridLetterFontPxChange !== "function") return;
            const v = clampInt(e.target.value, GRID_LETTER_FONT_MIN, GRID_LETTER_FONT_MAX);
            onGridLetterFontPxChange(v);
        },
        [onGridLetterFontPxChange]
    );

    const isAdminOrCreator =
        userRole === "ADMIN" || userRole === "CREATOR" || userRole === "MANAGER";
    if (!manualValidation || !isAdminOrCreator) {
        return null;
    }
    const roleDisplay =
        userRole === "ADMIN"
            ? "Admin"
            : userRole === "CREATOR"
              ? "Creator"
              : userRole === "MANAGER"
                ? "Manager"
                : null;

    const gridLetterTuning = typeof onGridLetterFontPxChange === "function";

    const renderTypeWordField = (textFieldSize) => (
        <TextField
            label={t("puzzle.playGrid.type-word-to-find")}
            variant="outlined"
            value={typedWord}
            onChange={onTypedWordChange}
            onKeyDown={onTypedWordKeyDown}
            placeholder={t("puzzle.playGrid.start-typing")}
            fullWidth
            size={textFieldSize}
            sx={{
                backgroundColor: SURFACE.sunken,
                "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                        borderColor: "#3BA666",
                    },
                    "&.Mui-focused fieldset": {
                        borderColor: "#3BA666",
                    },
                },
            }}
        />
    );

    const fieldsInner = (
        <>
            {gridLetterTuning && typeof gridLetterFontPx === "number" ? (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        gap: 1.5,
                        width: "100%",
                    }}
                >
                    <Box sx={{ width: "100%", minWidth: 0 }}>
                        {renderTypeWordField("small")}
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 1.5,
                            width: "100%",
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                m: 0,
                                flex: "1 1 160px",
                                minWidth: 0,
                                textAlign: "left",
                                color: ACCENT.light,
                                fontStyle: "italic",
                                fontWeight: 500,
                                fontSize: "0.75rem",
                                lineHeight: 1.4,
                            }}
                        >
                            {t("puzzle.playGrid.admin-help", { role: roleDisplay })}
                        </Typography>
                        <TextField
                            label={t("puzzle.playGrid.admin-font-label-letter")}
                            type="number"
                            size="small"
                            value={gridLetterFontPx}
                            onChange={handleLetterFontChange}
                            inputProps={{
                                min: GRID_LETTER_FONT_MIN,
                                max: GRID_LETTER_FONT_MAX,
                                step: 1,
                                "aria-label": t("puzzle.playGrid.admin-font-label-letter"),
                            }}
                            sx={{ width: 92, flexShrink: 0 }}
                        />
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={{
                        width: "70%",
                        maxWidth: "400px",
                        mx: "auto",
                    }}
                >
                    {renderTypeWordField()}
                </Box>
            )}
            {gridLetterTuning ? null : isAdminOrCreator ? (
                <Typography
                    variant="caption"
                    sx={{
                        mt: 1,
                        display: "block",
                        textAlign: "center",
                        color: ACCENT.light,
                        fontStyle: "italic",
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        lineHeight: 1.4,
                    }}
                >
                    {t("puzzle.playGrid.admin-help", { role: roleDisplay })}
                </Typography>
            ) : null}
        </>
    );

    return (
        <Box
            sx={{
                mt: 2,
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                /* Parent layout (e.g. GridPlayMain) already sets horizontal padding — avoid double inset vs grid */
                px: 0,
                mx: 0,
            }}
        >
            {gridLetterTuning ? (
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                        backgroundColor: SURFACE.sunken,
                        border: `1px solid ${SURFACE.border}`,
                        borderRadius: 1,
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        gap: 2,
                    }}
                >
                    {fieldsInner}
                </Box>
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    {fieldsInner}
                </Box>
            )}
        </Box>
    );
}

export default ManualValidationInput;
